import { transientTransactionAbort } from '../../shared/services/rtdb-transaction-errors';

/**
 * A link to a poll that does not (or no longer) exist - deleted poll, stale link, malformed
 * route params - is an expected user state, not an application error. Thrown by
 * PollService.initPollAndSubscribeData / enterPollByRoute and HeaderService.headerExist so the
 * call site can tell it apart from real failures (network, corrupt data, auth).
 *
 * Lives in its own file so HeaderService can import it without a circular
 * header.service <-> poll.service dependency; poll.service re-exports it.
 */
export class PollNotFoundError extends Error {
    constructor(public readonly ownerId: string, public readonly pollId: string, message: string) {
        super(message);
        this.name = 'PollNotFoundError';
        Object.setPrototypeOf(this, PollNotFoundError.prototype);
    }
}

/**
 * The poll could not be loaded and the user gets a retry instead of the endless spinner.
 * reason 'network': the network was unavailable (anonymous sign-up, header read, poll data) - an environment
 * state, the service logs a warning instead of reporting to /uilogging.
 * reason 'auth': the reads stayed permission_denied after the auth-drop retry (workorder-poll-init-auth-drop-hotfix-2
 * -slug-and-user-state-2026-10-29, task 2) - the service HAS reported it to /uilogging before throwing this.
 */
export class PollConnectionError extends Error {
    constructor(public readonly ownerId: string, public readonly pollId: string, public readonly cause?: unknown,
        public readonly reason: 'network' | 'auth' = 'network') {
        super(reason === 'auth' ? 'poll could not be loaded: read denied after the auth drop' : 'poll could not be loaded: no connection');
        this.name = 'PollConnectionError';
        Object.setPrototypeOf(this, PollConnectionError.prototype);
    }
}

/**
 * Firebase signals a failed network request with this code (auth SDK). The message check covers
 * places where the FirebaseError was already turned into an EssError or a string.
 */
export function isNetworkRequestFailed(error: unknown): boolean {
    const code = (error as { code?: string })?.code;
    if (code === 'auth/network-request-failed') return true;
    const message = typeof error === 'string' ? error : (error as Error)?.message;
    return (message ?? '').includes('auth/network-request-failed');
}

/** stable code of ConnectionLostError - surfaces check it with isConnectionLost, never the message */
export const CONNECTION_LOST_CODE = 'whocan/connection-lost';

/**
 * A database write gave no result because the connection dropped (or stayed down) - also after the one
 * retry (workorder-ensure-key-disconnect-retry-2026-10-30, task 3). An environment state like
 * `auth/network-request-failed`: the service has logged it as a warning, the surface shows the connection
 * message and keeps the action repeatable. `reason` is the abort reason of the last attempt.
 */
export class ConnectionLostError extends Error {
    public readonly code = CONNECTION_LOST_CODE;
    constructor(message: string, public readonly reason: string, public readonly cause?: unknown) {
        super(message);
        this.name = 'ConnectionLostError';
        Object.setPrototypeOf(this, ConnectionLostError.prototype);
    }
}

/**
 * True for a ConnectionLostError (also when only its code survived) and for a transaction abort that
 * slipped through uncaught (`disconnect`, `maxretry`, `set`, see transientTransactionAbort).
 */
export function isConnectionLost(error: unknown): boolean {
    if (error instanceof ConnectionLostError) return true;
    if ((error as { code?: string })?.code === CONNECTION_LOST_CODE) return true;
    return transientTransactionAbort(error) !== null;
}

/**
 * The header of a new poll could not be written - the first write and the single retry were both
 * rejected (workorder-persist-new-poll-single-flight-2026-09-22, task 4). HeaderService.persist has
 * already reported both rejections with the diagnostic fields, so callers only show the message and
 * do not report it a second time. The message is the translated 'internalError' for the snackbar.
 */
export class PollHeaderWriteError extends Error {
    constructor(public readonly ownerId: string, public readonly pollId: string, message: string) {
        super(message);
        this.name = 'PollHeaderWriteError';
        Object.setPrototypeOf(this, PollHeaderWriteError.prototype);
    }
}
